import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import QRCode from 'qrcode';

// Utils
import { MESSAGES } from '~/utils/message';
import { DISALLOWED_DOMAINS, ENGAGEMENT_TYPES, MERGE_UTM_PARAMS } from '~/utils/constants';

// Actions
import { uploadFile } from '~/redux/actions/qr-codes-actions';

// MUI Components
import Typography from '@mui/material/Typography';
import OutlinedInput from '@mui/material/OutlinedInput';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import {
  Box,
  InputLabel,
  TextField,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Tooltip,
  InputAdornment,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// Icons
import { UncheckedCIcon, CheckedCIcon, ContactInfoCIcon } from '~/assets/images';

// Components
import OlcDropdownSearch from '~/components/General/OlcDropdownSearch';
import { validURL } from '~/utils/helperFunctions';
import OlcButton from '~/components/General/OlcButton';
import OlcDropdownChevron from '~/components/General/OlcDropdownChevron';
import OlcFormModal from '~/components/General/OlcFormModal';

const QR_URL_TYPES = {
  STATIC: 'static',
  DYNAMIC: 'dynamic',
};

const URL_CUSTOM_FIELD_TYPE = 'url';

const QR_URL_TYPE_OPTIONS = [
  {
    id: QR_URL_TYPES.STATIC,
    title: 'Static URL',
    menuTitle: 'Static URL',
    description: 'Use when all QR scans should go to the same URL.',
  },
  {
    id: QR_URL_TYPES.DYNAMIC,
    title: 'Dynamic URL',
    menuTitle: 'Dynamic URL',
    description: 'Best when each contact has a unique URL assigned to them.',
  },
];

const formatCustomFieldUrlLabel = (customField = {}) => {
  const displayName = customField.value || customField.title || customField.label || customField.name;

  if (displayName) {
    return String(displayName).trim();
  }

  const fallbackLabel = customField.key
    ? customField.key.replace(/[{}]/g, '').replace(/\./g, '_')
    : '';
  const normalizedLabel = String(fallbackLabel).trim();

  if (!normalizedLabel) return '';

  return normalizedLabel;
};

const normalizeCustomFieldUrlLabel = (value = '') => {
  const normalizedLabel = String(value).trim().toLowerCase().replace(/\s+/g, '_');

  if (!normalizedLabel) return '';

  return normalizedLabel.startsWith('cf_') ? normalizedLabel : `cf_${normalizedLabel}`;
};

const DEFAULT_FORM_VALUES = {
  campaignName: '',
  urlField: QR_URL_TYPES.STATIC,
  customFieldId: null,
  qrUrl: '',
  utmSource: 'direct mail',
  utmMedium: 'QR Code',
  sendEmail: false,
  notificationEmail: '',
};

const CreateModal = (props) => {
  const formId = 'qr-code-form';
  const {
    openModal,
    handleCloseModal,
    onCreateQrCode,
    selectedQrCode,
    onUpdateQrCode,
    isEditMode = false,
    utmFields,
    customFields = [],
    allFields,
    utms,
    onCreateCustomFieldClick,
    pendingCustomFieldSelection,
    onPendingCustomFieldSelectionConsumed,
  } = props;

  const userEmail = useSelector((state) => state.userReducers?.user?.email || '');
  const isQrScanNotificationEnabled = useSelector(
    (state) => state.userReducers?.user?.subscribedPlan?.isDefault === false
  );

  const [loading, setLoading] = useState(false);
  const [customUtms, setCustomUtms] = useState({});
  const [openModalLocal, setOpenModalLocal] = useState(false);

  const [formValues, setFormValues] = useState(DEFAULT_FORM_VALUES);
  const [errors, setErrors] = useState({});

  const onClose = () => {
    handleCloseModal();
    setOpenModalLocal(false);
    setLoading(false);
    setCustomUtms({});
    setFormValues(DEFAULT_FORM_VALUES);
    setErrors({});
  };

  const handleChange = (field) => (event) => {
    const newValue = event.target.value;
    setFormValues((prev) => ({
      ...prev,
      [field]: newValue,
    }));
    if (errors[field]) {
      setErrors((prev) => {
        const { [field]: _removed, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleClearField = (field) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: '',
    }));
  };

  const getClearButtonAdornment = (fieldName, label) => {
    const fieldValue = formValues[fieldName];

    return (
      fieldValue?.trim() && (
        <InputAdornment
          className="qrClearInputAdornment"
          position="end"
          sx={{ width: '16px', marginRight: '8px' }}
        >
          <IconButton
            aria-label={`Clear ${label}`}
            className="cpClearInputBtn smallClearIcon qrClearInputBtn"
            size="small"
            type="button"
            tabIndex={-1}
            onClick={() => handleClearField(fieldName)}
            edge="end"
            sx={{ padding: '4px' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </InputAdornment>
      )
    );
  };

  const handleCheckboxChange = (event) => {
    if (!isQrScanNotificationEnabled) {
      setFormValues((prev) => ({
        ...prev,
        sendEmail: false,
        notificationEmail: '',
      }));
      return;
    }

    const isChecked = event.target.checked;
    setFormValues((prev) => ({
      ...prev,
      sendEmail: isChecked,
      notificationEmail: isChecked ? prev.notificationEmail || userEmail : '',
    }));
    setErrors((prev) => {
      const { notificationEmail: _removed, ...rest } = prev;
      return rest;
    });
  };

  const handleSelect = (utm, value) => {
    setCustomUtms((prev) => ({
      ...prev,
      [utm]: value ? { label: value.title || value.label } : null,
    }));
  };

  const handleUrlTypeSelect = (value) => {
    const nextUrlField = value?.id || QR_URL_TYPES.STATIC;
    setFormValues((prev) => ({
      ...prev,
      urlField: nextUrlField,
      customFieldId: nextUrlField === QR_URL_TYPES.DYNAMIC ? prev.customFieldId : null,
      qrUrl: prev.qrUrl,
    }));
    setErrors((prev) => {
      const {
        qrUrl: _qrUrl,
        customFieldId: _customFieldId,
        ...rest
      } = prev;
      return rest;
    });
  };

  const handleUrlFieldSelect = (value) => {
    setFormValues((prev) => ({
      ...prev,
      customFieldId: value?.id || null,
    }));
    if (errors.customFieldId) {
      setErrors((prev) => {
        const { customFieldId: _removed, ...rest } = prev;
        return rest;
      });
    }
  };

  const customUtmOptions = utmFields.map(({ label }) => ({ id: label, title: label }));
  const customFieldOptions = customFields
    .filter((customField) => customField.type === URL_CUSTOM_FIELD_TYPE)
    .map((customField) => ({
      id: customField.id,
      title: formatCustomFieldUrlLabel(customField),
    }))
    .filter(({ id, title }) => id && title);

  const appendUtmParameters = (utmField, defaultValue) => {
    let result = '';
    const field = MERGE_UTM_PARAMS.find((field) => field.key === utmField);
    if (field) {
      result = `${field.value}=${defaultValue}`;
    }
    return result;
  };

  const createCustomizeURL = (url) => {
    let customURL = url;
    let params = [];
    if (formValues.utmSource) {
      params.push(`utm_source=${formValues.utmSource.replace(/ /g, '_')}`);
    }
    if (formValues.utmMedium) {
      params.push(`utm_medium=${formValues.utmMedium.replace(/ /g, '_')}`);
    }
    if (formValues.campaignName) {
      params.push(`utm_campaign=${formValues.campaignName.replace(/ /g, '_')}`);
    }

    if (customUtms[utms[0]]?.label) {
      const orignalField = `{{${customUtms[utms[0]]?.label.replace('utm_', '').replace('_', '.').toUpperCase()}}}`;
      const defaultValue = allFields.find((field) => field.key === orignalField)?.defaultValue;

      const attachUtmKeys = appendUtmParameters(orignalField, defaultValue);
      const mergeUtmParams =
        attachUtmKeys && orignalField == '{{C.PHONE_NUMBER}}'
          ? encodeURIComponent(
              `${customUtms[utms[0]]?.label}=${defaultValue}&${attachUtmKeys}`
            ).replace(/[\s\(\)]/g, '')
          : attachUtmKeys
            ? `${customUtms[utms[0]]?.label}=${defaultValue}&${attachUtmKeys}`
            : `${customUtms[utms[0]]?.label}=${defaultValue}`;
      params.push(mergeUtmParams);
    }

    if (customUtms[utms[1]]?.label) {
      const orignalField = `{{${customUtms[utms[1]]?.label.replace('utm_', '').replace('_', '.').toUpperCase()}}}`;
      const defaultValue = allFields.find((field) => field.key === orignalField)?.defaultValue;
      const attachUtmKeys = appendUtmParameters(orignalField, defaultValue);
      const mergeUtmParams =
        attachUtmKeys && orignalField == '{{C.PHONE_NUMBER}}'
          ? encodeURIComponent(
              `${customUtms[utms[1]]?.label}=${defaultValue}&${attachUtmKeys}`
            ).replace(/[\s\(\)]/g, '')
          : attachUtmKeys
            ? `${customUtms[utms[1]]?.label}=${defaultValue}&${attachUtmKeys}`
            : `${customUtms[utms[1]]?.label}=${defaultValue}`;
      params.push(mergeUtmParams);
    }

    if (customUtms[utms[2]]?.label) {
      const orignalField = `{{${customUtms[utms[2]]?.label.replace('utm_', '').replace('_', '.').toUpperCase()}}}`;
      const defaultValue = allFields.find((field) => field.key === orignalField)?.defaultValue;
      const attachUtmKeys = appendUtmParameters(orignalField, defaultValue);
      const mergeUtmParams =
        attachUtmKeys && orignalField == '{{C.PHONE_NUMBER}}'
          ? encodeURIComponent(
              `${customUtms[utms[2]]?.label}=${defaultValue}&${attachUtmKeys}`
            ).replace(/[\s\(\)]/g, '')
          : attachUtmKeys
            ? `${customUtms[utms[2]]?.label}=${defaultValue}&${attachUtmKeys}`
            : `${customUtms[utms[2]]?.label}=${defaultValue}`;
      params.push(mergeUtmParams);
    }

    if (params.length > 0) {
      customURL += `/?${params.join('&')}`;
    }

    return encodeURI(customURL);
  };

  // create svg image for QR code for input text
  const getQR = (text) => {
    return new Promise((resolve, reject) => {
      QRCode.toDataURL(
        text || 'no-data',
        {
          type: 'image/png',
          margin: 0,
          color: {
            dark: '#000000',
            light: '#0000', // transparent background
          },
          scale: 10, // increase for higher resolution
        },
        (err, url) => {
          if (err) return reject(err);
          resolve(url); // returns base64 PNG image string
        }
      );
    });
  };

  // Converts base64 data URL to File
  const base64ToFile = (base64, filename) => {
    const arr = base64.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };

  const containsDisallowedDomains = (str) => {
    return DISALLOWED_DOMAINS.some((substring) => str.includes(substring));
  };

  const validateForm = () => {
    const trimmedCampaignName = formValues.campaignName.trim();
    const isDynamicUrl = formValues.urlField === QR_URL_TYPES.DYNAMIC;
    const trimmedQrUrl = formValues.qrUrl.trim();
    const trimmedNotificationEmail = formValues.notificationEmail.trim();
    const validationErrors = {};

    if (!trimmedCampaignName) {
      validationErrors.campaignName = 'Campaign name is required.';
    }

    if (!trimmedQrUrl) {
      validationErrors.qrUrl = isDynamicUrl ? 'Fallback URL is required.' : 'QR URL is required.';
    } else if (!validURL(trimmedQrUrl)) {
      validationErrors.qrUrl = isDynamicUrl ? 'Enter a valid fallback URL.' : 'Enter a valid URL.';
    } else if (containsDisallowedDomains(trimmedQrUrl)) {
      validationErrors.qrUrl = isDynamicUrl
        ? 'Fallback URL contains a disallowed domain.'
        : 'URL contains a disallowed domain.';
    }

    if (isDynamicUrl) {
      if (!formValues.customFieldId) {
        validationErrors.customFieldId = 'Select a URL field.';
      }
    }

    if (isQrScanNotificationEnabled && formValues.sendEmail) {
      if (!trimmedNotificationEmail) {
        validationErrors.notificationEmail = 'Notification email is required.';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedNotificationEmail)) {
        validationErrors.notificationEmail = 'Enter a valid email.';
      }
    }

    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return null;
    }

    if (
      formValues.campaignName !== trimmedCampaignName ||
      formValues.qrUrl !== trimmedQrUrl ||
      formValues.notificationEmail !== trimmedNotificationEmail
    ) {
      setFormValues((prev) => ({
        ...prev,
        campaignName: trimmedCampaignName,
        qrUrl: trimmedQrUrl,
        notificationEmail: trimmedNotificationEmail,
      }));
    }

    return {
      campaignName: trimmedCampaignName,
      qrUrl: trimmedQrUrl,
      notificationEmail: trimmedNotificationEmail,
    };
  };

  const createNewQrCode = async (event) => {
    try {
      event.preventDefault();

      const validatedValues = validateForm();
      if (!validatedValues) {
        return;
      }

      const {
        campaignName: trimmedCampaignName,
        qrUrl: trimmedQrUrl,
        notificationEmail: trimmedNotificationEmail,
      } = validatedValues;

      setErrors({});
      setLoading(true);
      // Transform customUtms to extract labels from objects
      const transformedCustomUtms = {};
      Object.entries(customUtms).forEach(([key, value]) => {
        if (value !== null && value.label) {
          transformedCustomUtms[key] = value;
        }
      });
      const isDynamicUrl = formValues.urlField === QR_URL_TYPES.DYNAMIC;
      const customQRUrl = createCustomizeURL(trimmedQrUrl);

      const ImageSrc = await getQR(customQRUrl);

      const file = base64ToFile(ImageSrc, `${trimmedCampaignName}-qr.png`);
      const uploadedFile = await uploadFile(file);
      const shouldSendNotificationEmail = isQrScanNotificationEnabled && formValues.sendEmail;

      const qrCodeData = {
        engagementType: ENGAGEMENT_TYPES.QR_CODE,
        name: trimmedCampaignName,
        url_field: formValues.urlField,
        custom_field_id: isDynamicUrl ? formValues.customFieldId : null,
        sendEmail: shouldSendNotificationEmail,
        notificationEmail: shouldSendNotificationEmail ? trimmedNotificationEmail : null,
        qrCodeJson: {
          utm_campaign_name: trimmedCampaignName,
          url: trimmedQrUrl,
          utm_source: formValues.utmSource || null,
          utm_medium: formValues.utmMedium || null,
          custom_utms: transformedCustomUtms,
          qrImagePath: uploadedFile,
        },
      };

      let response;
      if (isEditMode && selectedQrCode?.id) {
        response = await onUpdateQrCode(selectedQrCode.id, qrCodeData);
      } else {
        response = await onCreateQrCode(qrCodeData);
      }

      if (response.status === 200 || response.status === 201) {
        onClose();
      }
    } catch (error) {
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  useEffect(() => {
    if (!openModal) {
      // Reset all state when modal closes
      setLoading(false);
      setFormValues(DEFAULT_FORM_VALUES);
      setCustomUtms({});
      setOpenModalLocal(openModal);
      setErrors({});
    } else if (selectedQrCode) {
      // Set form values when editing
      setFormValues({
        campaignName: selectedQrCode?.qrCodeJson?.utm_campaign_name ?? '',
        urlField: selectedQrCode?.urlField || QR_URL_TYPES.STATIC,
        customFieldId: selectedQrCode?.customFieldId || null,
        qrUrl: selectedQrCode?.qrCodeJson?.url ?? '',
        utmSource: selectedQrCode?.qrCodeJson?.utm_source ?? '',
        utmMedium: selectedQrCode?.qrCodeJson?.utm_medium ?? '',
        sendEmail: isQrScanNotificationEnabled && Boolean(selectedQrCode?.sendEmail),
        notificationEmail: isQrScanNotificationEnabled
          ? (selectedQrCode?.notificationEmail ?? userEmail)
          : '',
      });
      setCustomUtms(selectedQrCode?.qrCodeJson?.custom_utms || {});
      setOpenModalLocal(openModal);
      setErrors({});
    } else if (openModal) {
      setFormValues((prev) => ({
        ...prev,
        sendEmail: false,
        notificationEmail: isQrScanNotificationEnabled ? userEmail : '',
      }));
      setOpenModalLocal(openModal);
      setErrors({});
    }
  }, [openModal, selectedQrCode, userEmail, isQrScanNotificationEnabled]);

  useEffect(() => {
    if (!pendingCustomFieldSelection || !customFieldOptions.length) {
      return;
    }

    const matchingOption = customFieldOptions.find((option) => {
      const pendingId = pendingCustomFieldSelection.id ?? pendingCustomFieldSelection.customFieldId;
      const pendingTitle =
        pendingCustomFieldSelection.title ||
        pendingCustomFieldSelection.label ||
        formatCustomFieldUrlLabel(pendingCustomFieldSelection);

      return (
        (pendingId && String(option.id) === String(pendingId)) ||
        (pendingTitle &&
          normalizeCustomFieldUrlLabel(option.title) === normalizeCustomFieldUrlLabel(pendingTitle))
      );
    });

    if (!matchingOption) {
      return;
    }

    setFormValues((prev) => ({
      ...prev,
      urlField: QR_URL_TYPES.DYNAMIC,
      customFieldId: matchingOption.id,
    }));
    setErrors((prev) => {
      const { customFieldId: _customFieldId, ...rest } = prev;
      return rest;
    });
    onPendingCustomFieldSelectionConsumed?.();
  }, [
    customFieldOptions,
    onPendingCustomFieldSelectionConsumed,
    pendingCustomFieldSelection,
  ]);

  const isQrScanNotificationChecked = isQrScanNotificationEnabled && formValues.sendEmail;
  const selectedUrlType = QR_URL_TYPE_OPTIONS.find(({ id }) => id === formValues.urlField);
  const selectedUrlField = customFieldOptions.find(
    ({ id }) => String(id) === String(formValues.customFieldId)
  );
  const isDynamicUrl = formValues.urlField === QR_URL_TYPES.DYNAMIC;

  return (
    <OlcFormModal
      open={openModalLocal}
      onClose={onClose}
      title={
        isEditMode
          ? MESSAGES.SETTINGS.QR_CODES.EDIT.TITLE
          : MESSAGES.SETTINGS.QR_CODES.ADD_NEW_MODAL.TITLE
      }
      className="qrCodeCreateModal"
      footer={
        <>
          <OlcButton variant="secondary" type="button" onClick={onClose} disabled={loading}>
            Cancel
          </OlcButton>
          <OlcButton variant="primary" type="submit" form={formId} disabled={loading}>
            {loading ? (
              <CircularProgress
                sx={{
                  color: 'white',
                  width: '20px !important',
                  height: '20px !important',
                }}
              />
            ) : (
              'Save'
            )}
          </OlcButton>
        </>
      }
    >
      <Box
        component="form"
        id={formId}
        className="qrCodeForm"
        onSubmit={createNewQrCode}
        noValidate
      >
        <Box className="modalFormWrapper qrWrpper">
          <Box className="modalFlexAreaQR">
            <Box>
              <Box>
                <InputLabel htmlFor="campaignName" className="formLabel">
                  Campaign Name<span className="requiredField">*</span>
                </InputLabel>
                <TextField
                  placeholder="Enter campaign name"
                  fullWidth
                  name="campaignName"
                  className="qrlgFields inputField formField"
                  onChange={handleChange('campaignName')}
                  value={formValues.campaignName.trimStart()}
                  autoComplete="off"
                />
                {Boolean(errors.campaignName) && (
                  <Typography className="errorMessage">{errors.campaignName}</Typography>
                )}
              </Box>
            </Box>

            <Box>
              <Box>
                <InputLabel htmlFor="qrUrl" className="formLabel">
                  QR URL<span className="requiredField">*</span>
                </InputLabel>
                <Box className="qrUrlFieldWrapper">
                  <Box className="olcDropdownSearch olcDropdownSearch--default multiSelectRedesign qrUrlTypeDropdown">
                    <Select
                      displayEmpty
                      inputProps={{ 'aria-label': 'QR URL Type' }}
                      value={selectedUrlType?.id || QR_URL_TYPES.STATIC}
                      onChange={(event) => {
                        handleUrlTypeSelect({ id: event.target.value });
                      }}
                      input={<OutlinedInput />}
                      renderValue={() => selectedUrlType?.title || 'Static'}
                      className="basicSelect"
                      MenuProps={{
                        disableScrollLock: true,
                        disablePortal: true,
                        marginThreshold: 0,
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left',
                        },
                        transformOrigin: {
                          vertical: 'top',
                          horizontal: 'left',
                        },
                        PaperProps: {
                          className: 'olcDropdownSearchOptions qrUrlTypeDropdownMenu',
                        },
                        MenuListProps: {
                          className: 'qrUrlTypeDropdownList',
                        },
                      }}
                      IconComponent={(props) => (
                        <OlcDropdownChevron
                          {...props}
                          style={{
                            ...props.style,
                            marginTop: '-2.5px',
                            right: '20px',
                            top: '50%',
                          }}
                        />
                      )}
                    >
                      {QR_URL_TYPE_OPTIONS.map((option) => (
                        <MenuItem key={option.id} value={option.id} disableRipple>
                          <Box>
                            <Typography className="qrUrlTypeOptionTitle">
                              {option.menuTitle}
                            </Typography>
                            <Typography className="qrUrlTypeOptionDescription">
                              {option.description}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                  {isDynamicUrl ? (
                    <OlcDropdownSearch
                      placeHolderText="Select URL Field"
                      searchFieldPlaceholderText="URL Field"
                      options={customFieldOptions}
                      selectedValue={selectedUrlField || null}
                      setSelectedValue={handleUrlFieldSelect}
                      className="qrUrlCustomFieldDropdown"
                      component="multipleIds"
                      noOptionText="No custom fields"
                      showSelectedValueTooltip
                      footerAction={
                        onCreateCustomFieldClick
                          ? {
                              label: 'Create New Custom Field',
                              onClick: onCreateCustomFieldClick,
                            }
                          : null
                      }
                    />
                  ) : (
                    <TextField
                      placeholder="Enter URL"
                      fullWidth
                      name="qrUrl"
                      className="qrlgFields inputField formField"
                      onChange={handleChange('qrUrl')}
                      value={formValues.qrUrl.trimStart()}
                      autoComplete="off"
                    />
                  )}
                </Box>
                {!isDynamicUrl && Boolean(errors.qrUrl) && (
                  <Typography className="errorMessage">{errors.qrUrl}</Typography>
                )}
                {Boolean(errors.customFieldId) && (
                  <Typography className="errorMessage">{errors.customFieldId}</Typography>
                )}
              </Box>
            </Box>

            {isDynamicUrl && (
              <Box>
                <InputLabel htmlFor="qrUrl" className="formLabel">
                  Fallback URL<span className="requiredField">*</span>
                </InputLabel>
                <TextField
                  placeholder="Enter Fallback URL"
                  fullWidth
                  name="qrUrl"
                  className="qrlgFields inputField formField"
                  onChange={handleChange('qrUrl')}
                  value={formValues.qrUrl.trimStart()}
                  autoComplete="off"
                />
                {Boolean(errors.qrUrl) && (
                  <Typography className="errorMessage">{errors.qrUrl}</Typography>
                )}
                <Box className="qrUrlHelperBox">
                  <img src={ContactInfoCIcon} alt="" className="qrUrlHelperIcon" />
                  <Typography className="qrUrlHelperText">
                    If a contact does not have a URL in the selected field, this fallback URL
                    ensures they still land on a company page.
                  </Typography>
                </Box>
              </Box>
            )}

            {!isDynamicUrl && (
              <Box className="qrUrlHelperBox">
                <img src={ContactInfoCIcon} alt="" className="qrUrlHelperIcon" />
                <Typography className="qrUrlHelperText">
                  All QR scans will be directed to this URL.
                </Typography>
              </Box>
            )}

            <div className="qrsmFieldsWrapper">
              <Box>
                <InputLabel htmlFor="utmSource" className="formLabel">
                  UTM Source
                </InputLabel>
                <TextField
                  placeholder="Enter UTM Source"
                  name="utmSource"
                  className="qrsmFields inputField formField"
                  onChange={handleChange('utmSource')}
                  value={formValues.utmSource.trimStart()}
                  autoComplete="off"
                  InputProps={{ endAdornment: getClearButtonAdornment('utmSource', 'UTM Source') }}
                />
              </Box>

              <Box>
                <InputLabel htmlFor="utmMedium" className="formLabel">
                  UTM Medium
                </InputLabel>
                <TextField
                  placeholder="Enter UTM Medium"
                  name="utmMedium"
                  className="qrsmFields inputField formField"
                  onChange={handleChange('utmMedium')}
                  value={formValues.utmMedium.trimStart()}
                  autoComplete="off"
                  InputProps={{ endAdornment: getClearButtonAdornment('utmMedium', 'UTM Medium') }}
                />
              </Box>
            </div>

            <Box className="qrNotificationCheckbox">
              <FormControlLabel
                className={`qrNotificationCheck${
                  isQrScanNotificationEnabled ? '' : ' qrNotificationCheckDisabled'
                }`}
                control={
                  <Checkbox
                    className="customCheckbox checked"
                    checked={isQrScanNotificationChecked}
                    disabled={!isQrScanNotificationEnabled}
                    onChange={handleCheckboxChange}
                    icon={<img src={UncheckedCIcon} alt="unchecked" />}
                    checkedIcon={<img src={CheckedCIcon} alt="checked" />}
                    style={{ pointerEvents: 'auto' }}
                    disableRipple={true}
                  />
                }
                label={
                  <Box component="span" className="qrNotificationLabel">
                    <span className="qrNotificationLabelText">Send scan notification email</span>
                    {!isQrScanNotificationEnabled && (
                      <Tooltip
                        title={MESSAGES.SETTINGS.QR_CODES.SCAN_NOTIFICATION_UPGRADE}
                        placement="bottom-start"
                        componentsProps={{
                          tooltip: {
                            sx: {
                              fontSize: '12px',
                              maxWidth: '260px',
                              padding: '8px !important',
                            },
                          },
                        }}
                      >
                        <img src={ContactInfoCIcon} alt="info" className="qrNotificationInfoIcon" />
                      </Tooltip>
                    )}
                  </Box>
                }
              />
            </Box>

            {isQrScanNotificationChecked && (
              <Box>
                <InputLabel htmlFor="notificationEmail" className="formLabel">
                  Notification Email<span className="requiredField">*</span>
                </InputLabel>
                <TextField
                  placeholder="Enter notification email"
                  fullWidth
                  name="notificationEmail"
                  className="qrlgFields inputField formField"
                  onChange={handleChange('notificationEmail')}
                  value={formValues.notificationEmail.trimStart()}
                  autoComplete="off"
                />
                {Boolean(errors.notificationEmail) && (
                  <Typography className="errorMessage">{errors.notificationEmail}</Typography>
                )}
              </Box>
            )}

            {utms?.map((utm, idx) => {
              return (
                <Box className="qrlgDropdown" key={idx}>
                  <Box className="olcSelectBox">
                    <InputLabel htmlFor="customUtm1" className="formLabel">
                      {utm.toUpperCase().replace(/\_/g, ' ')}:
                    </InputLabel>
                    <OlcDropdownSearch
                      placeHolderText={`Select Custom UTM ${idx + 1}`}
                      searchFieldPlaceholderText={`Custom UTM ${idx + 1}`}
                      options={customUtmOptions}
                      selectedValue={
                        customUtms[utm]?.label
                          ? {
                              id: customUtms[utm].label,
                              title: customUtms[utm].label,
                            }
                          : null
                      }
                      setSelectedValue={(value) => handleSelect(utm, value)}
                      className="qrCustomUtmDropdown"
                      clearable
                    />
                  </Box>
                </Box>
              );
            })}

            {isEditMode && (
              <Box>
                <Typography className="helperText">
                  Any changes you make to this QR code will apply immediately. If the <br /> code
                  has already been mailed, anyone who scans it after your update
                  <br /> will see the new settings.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </OlcFormModal>
  );
};

export default CreateModal;
